import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  LockClosedIcon,
  BellAlertIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { listAppVersions, createAppVersion, updateAppVersion, deleteAppVersion } from "../api/appVersionsAdmin";

const PLATFORMS = [
  { value: "android", label: "Android" },
  { value: "ios", label: "iOS" },
];
const VERSION_RE = /^\d+\.\d+\.\d+$/;

const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

const cmp = (a, b) => {
  const x = String(a).split(".").map((n) => parseInt(n, 10) || 0);
  const y = String(b).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    if ((x[i] || 0) !== (y[i] || 0)) return (x[i] || 0) < (y[i] || 0) ? -1 : 1;
  }
  return 0;
};

/**
 * Что увидит пользователь с версией `current`. Точная копия логики
 * mobile-app/lib/app_version/application/app_version_service.dart + backend update_required.
 * → "none" | "optional" | "force"
 */
export const simulate = (cfg, current) => {
  if (!cfg || !VERSION_RE.test(current || "")) return null;
  const { version, min_version: min, force_update: force } = cfg;
  if (version && cmp(current, version) > 0) return "none";
  const updateRequired = Boolean((min && cmp(current, min) < 0) || force);
  const isTarget = version ? cmp(current, version) === 0 : updateRequired;
  if (isTarget) {
    if (min) return cmp(current, min) >= 0 ? "none" : "force";
    return updateRequired ? (force ? "force" : "optional") : "none";
  }
  if (!updateRequired) return "none";
  return force ? "force" : "optional";
};

const OUTCOME = {
  none: { label: "Окно не показывается", cls: "text-text-secondary", icon: EyeSlashIcon },
  optional: { label: "Окно «Доступно обновление» — можно закрыть (1 раз на версию)", cls: "text-amber-500", icon: BellAlertIcon },
  force: { label: "Обязательное обновление — без него приложением пользоваться нельзя", cls: "text-rose-500", icon: LockClosedIcon },
};

/** Опасные комбинации настроек */
const risks = (cfg) => {
  const out = [];
  if (!cfg) return out;
  if (cfg.force_update && !cfg.min_version)
    out.push(`Обязательное обновление без мин. версии: пользователи, у которых уже стоит ${cfg.version}, тоже получат неустранимое окно.`);
  if (cfg.min_version && cfg.version && cmp(cfg.min_version, cfg.version) > 0) out.push("Мин. версия больше публикуемой — обновиться до неё невозможно.");
  if (cfg.force_update && cfg.min_version) out.push("Обязательно для ВСЕХ версий ниже публикуемой, не только ниже минимальной.");
  return out;
};

const Outcome = ({ kind }) => {
  if (!kind) return <span className="text-xs text-text-muted">—</span>;
  const o = OUTCOME[kind];
  return (
    <span className={`inline-flex items-start gap-1.5 text-xs ${o.cls}`}>
      <o.icon className="mt-px h-4 w-4 shrink-0" />
      {o.label}
    </span>
  );
};

/** Примеры версий для таблицы «что увидят» */
const sampleVersions = (cfg) => {
  if (!cfg?.version) return [];
  const [a, b, c] = cfg.version.split(".").map((n) => parseInt(n, 10) || 0);
  const list = [];
  if (cfg.min_version) {
    const [x, y, z] = cfg.min_version.split(".").map((n) => parseInt(n, 10) || 0);
    list.push([`ниже мин. (напр. ${z > 0 ? `${x}.${y}.${z - 1}` : y > 0 ? `${x}.${y - 1}.9` : `${Math.max(x - 1, 0)}.9.9`})`, z > 0 ? `${x}.${y}.${z - 1}` : y > 0 ? `${x}.${y - 1}.9` : `${Math.max(x - 1, 0)}.9.9`]);
    if (cmp(cfg.min_version, cfg.version) < 0) list.push([`от мин. до публикуемой (${cfg.min_version})`, cfg.min_version]);
  } else {
    const older = c > 0 ? `${a}.${b}.${c - 1}` : b > 0 ? `${a}.${b - 1}.9` : `${Math.max(a - 1, 0)}.9.9`;
    list.push([`старее (напр. ${older})`, older]);
  }
  list.push([`= ${cfg.version}`, cfg.version]);
  return list;
};

const PlatformCard = ({ platform, active, onEdit, onPublish }) => {
  const [probe, setProbe] = useState("");
  const outcome = simulate(active, probe);
  const warn = risks(active);
  return (
    <section className="flex flex-col rounded-block border border-border-primary bg-bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border-primary px-5 py-3.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <DevicePhoneMobileIcon className="h-5 w-5 text-[#7C3AED]" />
          {platform.label}
        </h3>
        <button type="button" onClick={onPublish} className="inline-flex items-center gap-1 rounded-lg border border-border-primary px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-bg-tertiary">
          <PlusIcon className="h-3.5 w-3.5" /> Новая версия
        </button>
      </div>
      {active ? (
        <div className="flex flex-1 flex-col gap-4 p-5">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs text-text-muted">Действует сейчас</p>
              <p className="font-mono text-3xl font-bold text-text-primary">{active.version}</p>
            </div>
            <div className="text-right text-xs text-text-secondary">
              <p>
                мин. версия: <span className="font-mono text-text-primary">{active.min_version || "—"}</span>
              </p>
              <p className={active.force_update ? "font-semibold text-rose-500" : ""}>{active.force_update ? "обязательное" : "не обязательное"}</p>
            </div>
          </div>
          {active.update_message && <p className="rounded-lg bg-bg-primary px-3 py-2 text-sm italic text-text-secondary">«{active.update_message}»</p>}
          {warn.map((w) => (
            <p key={w} className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" /> {w}
            </p>
          ))}
          <div className="mt-auto rounded-lg border border-border-primary p-3">
            <label className="flex items-center gap-2 text-xs text-text-secondary">
              Проверить версию пользователя
              <input value={probe} onChange={(e) => setProbe(e.target.value.trim())} placeholder="1.0.30" className="w-24 rounded-md border border-border-primary bg-bg-primary px-2 py-1 font-mono text-xs text-text-primary focus:outline-none" />
            </label>
            <div className="mt-2 min-h-[1.25rem]">{probe && (outcome ? <Outcome kind={outcome} /> : <span className="text-xs text-text-muted">Формат X.Y.Z</span>)}</div>
          </div>
          <button type="button" onClick={() => onEdit(active)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-tertiary">
            <PencilSquareIcon className="h-4 w-4" /> Изменить условия
          </button>
        </div>
      ) : (
        <div className="px-5 py-10 text-center text-sm text-text-muted">Нет активной версии — окно обновления не показывается никому.</div>
      )}
    </section>
  );
};

const Editor = ({ item, platform, onClose, onSaved }) => {
  const editing = Boolean(item?.id);
  const [form, setForm] = useState({
    platform: item?.platform || platform || "android",
    version: item?.version || "",
    min_version: item?.min_version || "",
    force_update: Boolean(item?.force_update),
    update_message: item?.update_message || "",
    is_active: item ? item.is_active !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cfg = { ...form, min_version: form.min_version || null };
  const valid = VERSION_RE.test(form.version) && (!form.min_version || VERSION_RE.test(form.min_version));
  const warn = valid ? risks(cfg) : [];
  const blocking = form.force_update && !form.min_version;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const save = async (e) => {
    e.preventDefault();
    if (!VERSION_RE.test(form.version)) return setError("Версия в формате X.Y.Z, например 1.2.0");
    if (form.min_version && !VERSION_RE.test(form.min_version)) return setError("Мин. версия в формате X.Y.Z");
    if (form.min_version && cmp(form.min_version, form.version) > 0) return setError("Мин. версия не может быть больше публикуемой.");
    if (blocking) return setError("Для обязательного обновления укажите мин. версию — иначе пользователи с последней версией застрянут на окне обновления.");
    setSaving(true);
    setError(null);
    const body = { ...form, min_version: form.min_version || null, update_message: form.update_message.trim() || null };
    try {
      if (editing) await updateAppVersion(item.id || item.version_id, body);
      else await createAppVersion(body);
      onSaved(`${form.platform === "ios" ? "iOS" : "Android"} ${form.version} ${editing ? "сохранена" : "опубликована"}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form onSubmit={save} role="dialog" aria-modal="true" className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-block border border-border-primary bg-bg-card text-left shadow-lg">
        <div className="flex items-center justify-between border-b border-border-primary px-5 py-3.5">
          <h3 className="text-base font-semibold text-text-primary">{editing ? "Изменить версию" : "Опубликовать версию"}</h3>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 overflow-y-auto p-5">
          <div className="flex rounded-lg border border-border-primary bg-bg-primary p-0.5">
            {PLATFORMS.map((p) => (
              <button key={p.value} type="button" onClick={() => set("platform", p.value)} className={`flex-1 rounded-md py-1.5 text-sm font-semibold ${form.platform === p.value ? "bg-accent-grad text-white" : "text-text-secondary"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Версия в магазине *</span>
              <input autoFocus value={form.version} onChange={(e) => set("version", e.target.value.trim())} placeholder="1.2.0" className={`${inputCls} font-mono`} />
              <span className="mt-0.5 block text-[11px] text-text-muted">Должна уже быть опубликована в {form.platform === "ios" ? "App Store" : "Google Play"}</span>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Минимальная версия</span>
              <input value={form.min_version} onChange={(e) => set("min_version", e.target.value.trim())} placeholder="1.0.0" className={`${inputCls} font-mono`} />
              <span className="mt-0.5 block text-[11px] text-text-muted">Ниже неё — окно обновления</span>
            </label>
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border-primary p-3">
            <input type="checkbox" checked={form.force_update} onChange={(e) => set("force_update", e.target.checked)} className="mt-0.5 h-4 w-4 accent-rose-600" />
            <span>
              <span className="block text-sm font-medium text-text-primary">Обязательное обновление</span>
              <span className="block text-xs text-text-secondary">Окно нельзя закрыть — пользователь не сможет работать в приложении, пока не обновится.</span>
            </span>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Текст в окне обновления</span>
            <textarea rows={3} value={form.update_message} onChange={(e) => set("update_message", e.target.value)} placeholder="Что нового в этой версии" className={`${inputCls} resize-y`} />
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
            Активна (приложение использует последнюю активную версию платформы)
          </label>

          {valid && (
            <div className="rounded-lg border border-border-primary">
              <p className="border-b border-border-primary px-3 py-2 text-xs font-semibold text-text-primary">Что увидят пользователи</p>
              <ul className="divide-y divide-border-primary">
                {sampleVersions(cfg).map(([label, v]) => (
                  <li key={label} className="flex items-start justify-between gap-3 px-3 py-2">
                    <span className="shrink-0 text-xs text-text-secondary">{label}</span>
                    <Outcome kind={simulate(cfg, v)} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {warn.map((w) => (
            <p key={w} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${blocking && w.startsWith("Обязательное обновление без") ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-amber-500/30 bg-amber-500/10 text-amber-500"}`}>
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" /> {w}
            </p>
          ))}
          {error && (
            <p role="alert" className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" /> {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border-primary px-5 py-3">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
            Отмена
          </button>
          <button type="submit" disabled={saving} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${form.force_update ? "bg-rose-600 hover:bg-rose-700" : "bg-accent-grad hover:opacity-90"}`}>
            {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {editing ? "Сохранить" : form.force_update ? "Опубликовать (обязательно)" : "Опубликовать"}
          </button>
        </div>
      </form>
    </div>
  );
};

const AppVersionManagement = () => {
  const [versions, setVersions] = useState([]);
  const [state, setState] = useState("loading");
  const [platformFilter, setPlatformFilter] = useState("");
  const [editor, setEditor] = useState(null); // { item, platform }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    setState((s) => (s === "ready" ? "ready" : "loading"));
    try {
      setVersions(await listAppVersions());
      setState("ready");
    } catch (e) {
      console.error("Error fetching app versions:", e);
      setState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Backend берёт последнюю по created_at активную версию платформы
  const activeByPlatform = useMemo(() => {
    const out = {};
    PLATFORMS.forEach(({ value }) => {
      out[value] = versions
        .filter((v) => v.platform === value && v.is_active !== false)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
    });
    return out;
  }, [versions]);

  const history = versions
    .filter((v) => !platformFilter || v.platform === platformFilter)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const idOf = (v) => v.id || v.version_id;

  const toggleActive = async (v) => {
    setBusyId(idOf(v));
    try {
      await updateAppVersion(idOf(v), { is_active: v.is_active === false });
      setToast({ type: "success", text: `${v.version} ${v.is_active === false ? "активирована" : "отключена"}` });
      load();
    } catch (e) {
      setToast({ type: "error", text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (v) => {
    setBusyId(idOf(v));
    try {
      await deleteAppVersion(idOf(v));
      setToast({ type: "success", text: `Версия ${v.version} удалена` });
      setConfirmDelete(null);
      load();
    } catch (e) {
      setToast({ type: "error", text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Версии приложения</h2>
          <p className="mt-0.5 text-sm text-text-secondary">Окно «Доступно обновление» в мобильном приложении. Действует последняя активная версия каждой платформы.</p>
        </div>
        <button type="button" onClick={load} title="Обновить" aria-label="Обновить" className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary">
          <ArrowPathIcon className={`h-5 w-5 ${state === "loading" ? "animate-spin" : ""}`} />
        </button>
      </div>

      {toast && (
        <div role={toast.type === "error" ? "alert" : "status"} className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"}`}>
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {state === "error" ? (
        <div className="flex flex-col items-center gap-3 rounded-block border border-border-primary bg-bg-card px-6 py-14 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
          <p className="text-sm text-text-secondary">Не удалось загрузить версии</p>
          <button type="button" onClick={load} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
            Повторить
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {state === "loading" && versions.length === 0
              ? [1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-block border border-border-primary bg-bg-card" />)
              : PLATFORMS.map((p) => (
                  <PlatformCard key={p.value} platform={p} active={activeByPlatform[p.value]} onEdit={(item) => setEditor({ item })} onPublish={() => setEditor({ item: null, platform: p.value })} />
                ))}
          </div>

          <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-border-primary px-5 py-3">
              <h3 className="text-sm font-semibold text-text-primary">История</h3>
              <div className="flex rounded-lg border border-border-primary bg-bg-primary p-0.5">
                {[{ value: "", label: "Все" }, ...PLATFORMS].map((p) => (
                  <button key={p.value || "all"} type="button" onClick={() => setPlatformFilter(p.value)} className={`rounded-md px-3 py-1 text-xs font-semibold ${platformFilter === p.value ? "bg-bg-card text-text-primary shadow-sm" : "text-text-secondary"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {history.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-text-muted">{state === "loading" ? "Загрузка…" : "Версий пока нет"}</p>
            ) : (
              <ul className="divide-y divide-border-primary">
                {history.map((v) => {
                  const id = idOf(v);
                  const current = activeByPlatform[v.platform] && idOf(activeByPlatform[v.platform]) === id;
                  const inactive = v.is_active === false;
                  return (
                    <li key={id} className={`group flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 ${busyId === id ? "opacity-50" : ""}`}>
                      <span className="w-16 text-xs font-medium text-text-secondary">{v.platform === "ios" ? "iOS" : "Android"}</span>
                      <span className={`w-20 font-mono text-sm font-semibold ${inactive ? "text-text-muted line-through" : "text-text-primary"}`}>{v.version}</span>
                      <span className="w-28 text-xs text-text-secondary">
                        мин. <span className="font-mono">{v.min_version || "—"}</span>
                      </span>
                      <span className="flex flex-1 flex-wrap items-center gap-1.5">
                        {current && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-px text-[11px] font-semibold text-emerald-500">
                            <CheckCircleIcon className="h-3.5 w-3.5" /> действует
                          </span>
                        )}
                        {v.force_update && <span className="rounded-md border border-rose-500/30 bg-rose-500/10 px-1.5 py-px text-[11px] font-semibold text-rose-500">обязательное</span>}
                        {inactive && <span className="rounded-md border border-border-primary px-1.5 py-px text-[11px] text-text-muted">отключена</span>}
                        {v.update_message && <span className="truncate text-xs text-text-muted" title={v.update_message}>«{v.update_message}»</span>}
                      </span>
                      <span className="text-xs text-text-muted">{v.created_at ? new Date(v.created_at).toLocaleDateString("ru-RU") : ""}</span>
                      {confirmDelete === id ? (
                        <span className="flex items-center gap-1.5">
                          <button type="button" autoFocus onClick={() => remove(v)} className="rounded-lg bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-700">
                            Удалить
                          </button>
                          <button type="button" onClick={() => setConfirmDelete(null)} className="rounded-lg border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-tertiary">
                            Нет
                          </button>
                        </span>
                      ) : (
                        <span className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100">
                          <button type="button" onClick={() => toggleActive(v)} className="rounded-lg px-2 py-1 text-xs font-medium text-text-secondary hover:bg-bg-tertiary">
                            {inactive ? "Включить" : "Отключить"}
                          </button>
                          <button type="button" onClick={() => setEditor({ item: v })} title="Изменить" aria-label="Изменить" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => setConfirmDelete(id)} title="Удалить" aria-label="Удалить" className="rounded-lg p-1.5 text-text-muted hover:bg-rose-500/10 hover:text-rose-500">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {editor && (
        <Editor
          item={editor.item}
          platform={editor.platform}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            setEditor(null);
            setToast({ type: "success", text: message });
            load();
          }}
        />
      )}
    </div>
  );
};

export default AppVersionManagement;
